import { forwardRef, useCallback, useState } from 'react'
import { Button } from '@platform/ui-kit'
import { m, LazyMotion, domAnimation, useAnimation } from 'framer-motion'

import { Rocket, AstronautHelmet, Telescope } from '../../../assets/icons'

import styles from './index.module.scss'

// Animations
const SCROLL_INTO_VIEW = {
  start: { y: '100%', opacity: 0 },
  end: {
    y: '0%',
    opacity: 1,
    transition: { type: 'spring', bounce: 0 },
  },
}

const OPACITY_ANIMATION = {
  start: { opacity: 0 },
  end: {
    opacity: 1,
    transition: { type: 'spring', bounce: 0 },
  },
}

const TITLE_ANIMATION = {
  start: { opacity: 0.01, y: '50%' },
  end: {
    y: '0%',
    opacity: 1,
    transition: { type: 'spring', bounce: 0 },
  },
}

const TITLE_CONTAINER_ANIMATION = {
  start: {
    y: '100%',
  },
  end: {
    y: '0%',
    transition: { type: 'spring', bounce: 0 },
  },
}

const CARD_1 = {
  start: { y: '100%', opacity: 0 },
  end: {
    y: '0%',
    opacity: 1,
    transition: { type: 'spring', bounce: 0, duration: 1 },
  },
}
const CARD_2 = {
  start: { y: '100%', opacity: 0 },
  end: {
    y: '0%',
    opacity: 1,
    transition: { type: 'spring', bounce: 0, duration: 1.5 },
  },
}
const CARD_3 = {
  start: { y: '100%', opacity: 0 },
  end: {
    y: '0%',
    opacity: 1,
    transition: { type: 'spring', bounce: 0, duration: 2 },
  },
}

export const ForTalentsGetStarted = forwardRef<HTMLElement>(() => {
  const headingControl = useAnimation()
  const descriptionControl = useAnimation()
  const headingContainerControl = useAnimation()
  const cards1Control = useAnimation()
  const cards2Control = useAnimation()
  const cards3Control = useAnimation()
  const bottomSectionControl = useAnimation()
  const bottomSectionContentControl = useAnimation()
  const [wasAnimationTriggered, setWasAnimationTriggered] = useState(false)

  const onViewportEnter = useCallback(() => {
    if (wasAnimationTriggered) {
      return
    }
    setWasAnimationTriggered(true)

    headingControl.start('end').then(() => {
      descriptionControl.start('end')
      headingContainerControl.start('end').then(() => {
        bottomSectionControl.start('end')
        cards1Control.start('end')
        cards2Control.start('end')
        cards3Control.start('end').then(() => {
          bottomSectionContentControl.start('end')
        })
      })
    })
  }, [
    wasAnimationTriggered,
    headingControl,
    descriptionControl,
    headingContainerControl,
    cards1Control,
    cards2Control,
    cards3Control,
    bottomSectionControl,
    bottomSectionContentControl,
  ])

  const CARDS = [
    {
      title: 'Sign up',
      description:
        'Fill out your profile, and tell us about your work preferences and salary expectations.',
      icon: <Rocket className={styles['card-icon']} />,
      animation: {
        animate: cards1Control,
        variants: CARD_1,
        initial: 'start',
      },
    },
    {
      title: 'Validate skills',
      description: 'Engage in our technical challenge and interviews to join our talent pool.',
      icon: <AstronautHelmet className={styles['card-icon']} />,
      animation: {
        animate: cards2Control,
        variants: CARD_2,
        initial: 'start',
      },
    },
    {
      title: 'Match time',
      description:
        'We match you with job opportunities according to your profile skills and preferences.',
      icon: <Telescope className={styles['card-icon']} />,
      animation: {
        animate: cards3Control,
        variants: CARD_3,
        initial: 'start',
      },
    },
  ]

  return (
    <LazyMotion features={domAnimation} strict>
      <m.section
        className={styles['wrapper']}
        viewport={{ once: true, amount: 0.3 }}
        initial="start"
        onViewportEnter={onViewportEnter}
      >
        <div className={styles['content']}>
          <m.div
            variants={TITLE_CONTAINER_ANIMATION}
            initial="start"
            animate={headingContainerControl}
          >
            <m.h2
              className={styles['title']}
              initial="start"
              variants={TITLE_ANIMATION}
              animate={headingControl}
            >
              How to get started with Olby
            </m.h2>
            <m.p
              className={styles['description']}
              animate={descriptionControl}
              variants={OPACITY_ANIMATION}
              initial="start"
            >
              An amazing journey lies ahead — our process was designed to ensure that both tech
              talent and company share aligned skills, values, and culture.
            </m.p>
          </m.div>
          <div className={styles['cards-wrapper']}>
            {CARDS.map(({ title, description, icon, animation }, index) => (
              <m.div {...animation} className={styles['card']}>
                <div className={styles['card-top']}>
                  <div className={styles['step']}>Step {index + 1}</div>
                  {icon}
                </div>
                <div className={styles['card-content']}>
                  <h3 className={styles['card-title']}>{title}</h3>
                  <p className={styles['card-description']}>{description}</p>
                </div>
              </m.div>
            ))}
          </div>
        </div>
        <m.div
          className={styles['bottom-section']}
          variants={SCROLL_INTO_VIEW}
          initial="start"
          animate={bottomSectionControl}
        >
          <m.div animate={bottomSectionContentControl} initial="start" variants={SCROLL_INTO_VIEW}>
            <p className={styles['additional-text']}>
              Once we find a fit, we'll share with you to understand your interest. <br />
              Then, if the company also wants to move the process forward,{' '}
              <span className={styles['bold']}>it's a match!</span>
            </p>
            <Button size="large" variant="primary" href="/talent">
              Join Olby
            </Button>
          </m.div>
        </m.div>
      </m.section>
    </LazyMotion>
  )
})
